/**
 * @file.
 * Contains index utils(for JS).
 */
import { MT_RET_REQUEST_DONE } from 'common/constants/trading.constants';
import { useEffect, useRef } from 'react';
import { captureError, TargetValueError } from './error.util';

export const last = (arr = []) => {
  const length = arr.length || 1;
  return arr[length - 1];
};

export const first = (arr = []) => (arr.length ? arr[0] : null);

export function isErrorMessage(message = {}) {
  return message.result !== MT_RET_REQUEST_DONE.code;
}
/* useEffect without first (initial) change */
export const useDidMountEffect = (func, deps) => {
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) func();
    else didMount.current = true;
  }, deps);
};

export const isEqualObjects = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const makeRegServiceErrorMsg = (error) => {
  switch (error) {
    case 'servererror':
      return 'Something went wrong. Please try again.';
    case 'usernotfound':
      return 'Email and password mismatch. Please try again.';
    case 'invalidemail':
      return 'Please enter correct email address.';
    case 'missingparam':
      return 'Please, fill in all fields and try again.';
    default:
      return 'Unknown error. Please, contact support.';
  }
};

export const getRandomInt = (min, max) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const arrayIntersection = (a, b) => {
  const setB = new Set(b);
  return [...new Set(a)].filter((x) => setB.has(x));
};

export const handleTargetValue = (val) => {
  if (typeof val !== 'string') {
    captureError(
      new TargetValueError(
        `value type is ${typeof val}, value: ${JSON.stringify(val)}`
      )
    );
    return String(val);
  }
  return val.slice(0);
};

export const reverseDate = (date) => {
  const [year, month, day] = date.split('-');
  return `${day}-${month}-${year}`;
};

export function round(num, decimalPlaces = 0) {
  num = Math.round(`${num}e${decimalPlaces}`);
  return Number(`${num}e${-decimalPlaces}`);
}

export function preferredOrder(obj, order) {
  const newObject = {};
  for (let i = 0; i < order.length; i++) {
    if (obj.hasOwnProperty(order[i])) {
      newObject[order[i]] = obj[order[i]];
    }
  }
  return newObject;
}

export function paramsToObject(entries) {
  const result = {};
  for (const [key, value] of entries) {
    result[key] = value;
  }
  return result;
}

/**
 * Check json.
 * @param { string } str
 * @returns boolean.
 */
export function isJson(str) {
  try {
    return !!JSON.parse(str);
  } catch (e) {
    return false;
  }
}

export const getRandomElementInArray = (array) =>
  array[Math.floor(Math.random() * array.length)];

/**
 * date of birth regexp.
 */
export const dateOfBirthRegExp =
  /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]|(?:Jan|Mar|May|Jul|Aug|Oct|Dec)))\1|(?:(?:29|30)(\/|-|\.)(?:0?[1,3-9]|1[0-2]|(?:Jan|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)(?:0?2|(?:Feb))\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9]|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep))|(?:1[0-2]|(?:Oct|Nov|Dec)))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/;

export const setUndefinedToFalse = (value) =>
  value === undefined ? false : value;

export const clamp = (value, min, max = 0) =>
  Math.min(Math.max(value, min), max);

export const toFixedWithFixedLength = (value, length) => {
  const fixedValue = value.toFixed(length);
  if (fixedValue.length > length + 1) {
    return fixedValue.slice(0, length + 1);
  }
  return fixedValue;
};

export const compareBitMasks = (a, b) => {
  // eslint-disable-next-line no-bitwise
  return !!(a & b);
};
export const withSign = (value) => {
  if (Number(value) > 0) return `+${value}`;
  if (value.includes('-')) {
    return value;
  }
  return `+${value}`;
};
